import {EmptyString, isNonEmptyString, NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import * as I from 'immutable';
import * as RI from '../RiderInformation';
import {ToPartialMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';

const riderCodes: RI.NewYorkRiderCode[] = [
  {
    code: 'APD',
    displayName: 'APD',
    units: [0.5, 1, 1.5, 2, 2.5, 3],
  },
];

type AccidentProps = {
  plan: StringUnion;
  planType: StringUnion;
  premium: StringUnion;
  calculatedPremium: StringUnion;
  monthlySalary: StringUnion;
  cwa: StringUnion;
  section125: boolean;
  riderInformation: RI.default;
  baseUnits: StringUnion;
};

export class InvalidNewYorkAccident {
  public readonly key = 'InvalidNewYorkAccident';

  constructor(
    public readonly plan: StringUnion,
    public readonly planType: StringUnion,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly monthlySalary: StringUnion,
    public readonly cwa: StringUnion,
    public readonly section125: boolean,
    public readonly riderInformation: RI.default,
    public readonly baseUnits: StringUnion
  ) {}
}

export class ValidNewYorkAccident {
  public static createApp({
    plan,
    planType,
    premium,
    calculatedPremium,
    monthlySalary,
    cwa,
    section125,
    riderInformation,
    baseUnits,
  }: AccidentProps) {
    return this.create(
      plan,
      planType,
      premium,
      calculatedPremium,
      monthlySalary,
      cwa,
      section125,
      riderInformation,
      baseUnits
    );
  }

  public static create(
    plan: StringUnion,
    planType: StringUnion,
    premium: StringUnion,
    calculatedPremium: StringUnion,
    monthlySalary: StringUnion,
    cwa: StringUnion,
    section125: boolean,
    riderInformation: RI.default,
    baseUnits: StringUnion
  ) {
    return isNonEmptyString(plan) &&
      planType &&
      isNonEmptyString(premium) &&
      isNonEmptyString(monthlySalary) &&
      isNonEmptyString(cwa) &&
      section125 &&
      riderInformation.codes.size > 0 &&
      riderInformation.amounts.size > 0
      ? new ValidNewYorkAccident(
          plan,
          planType,
          premium,
          calculatedPremium,
          monthlySalary,
          cwa,
          section125,
          riderInformation,
          baseUnits
        )
      : new InvalidNewYorkAccident(
          plan,
          planType,
          premium,
          calculatedPremium,
          monthlySalary,
          cwa,
          section125,
          riderInformation,
          baseUnits
        );
  }

  public readonly key = 'ValidNewYorkAccident';
  constructor(
    public readonly plan: StringUnion,
    public readonly planType: StringUnion,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly monthlySalary: StringUnion,
    public readonly cwa: StringUnion,
    public readonly section125: boolean,
    public readonly riderInformation: RI.default,
    public readonly baseUnits: StringUnion
  ) {}
}

export type NewYorkAccident = InvalidNewYorkAccident | ValidNewYorkAccident;

export type NewYorkAccidentOptions<TReturn> = ToPartialMatchOptions<NewYorkAccident, 'key', TReturn>;

export const matchNewYorkAccident = <TReturn>(options: NewYorkAccidentOptions<TReturn>) => (
  app: NewYorkAccident
): TReturn => {
  switch (app.key) {
    case 'InvalidNewYorkAccident':
      return options.InvalidNewYorkAccident ? options.InvalidNewYorkAccident(app) : options.default(app);
    case 'ValidNewYorkAccident':
      return options.ValidNewYorkAccident ? options.ValidNewYorkAccident(app) : options.default(app);
  }
};

export const EmptyNewYorkAccident = new InvalidNewYorkAccident(
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  false,
  new RI.default(I.List<string>(), I.List<number>(), riderCodes),
  new EmptyString()
);

export const changePlan = (plan: string) => (app: NewYorkAccident) =>
  ValidNewYorkAccident.createApp({
    ...app,
    plan: NonEmptyString.create(plan),
    riderInformation: new RI.default(I.List<string>(), I.List<number>(), riderCodes),
  });

export const changePlanType = (planType: string) => (app: NewYorkAccident) =>
  ValidNewYorkAccident.createApp({...app, planType: NonEmptyString.create(planType)});

export const changePremium = (premium: string) => (app: NewYorkAccident) =>
  ValidNewYorkAccident.createApp({...app, premium: NonEmptyString.create(premium)});

export const changeCalculatedPremium = (calculatedPremium: string) => (app: NewYorkAccident) =>
  ValidNewYorkAccident.createApp({...app, calculatedPremium: NonEmptyString.create(calculatedPremium)});

export const changeMonthlySalary = (monthlySalary: string) => (app: NewYorkAccident) =>
  ValidNewYorkAccident.createApp({...app, monthlySalary: NonEmptyString.create(monthlySalary)});

export const changeCWA = (cwa: string) => (app: NewYorkAccident) =>
  ValidNewYorkAccident.createApp({...app, cwa: NonEmptyString.create(cwa)});

export const changeSection125 = (section125: boolean) => (app: NewYorkAccident) =>
  ValidNewYorkAccident.createApp({...app, section125});

export const changeRiderInformation = (riderAmount: number, riderCode: string, index: number) => (
  app: NewYorkAccident
) => {
  const riderInformation = RI.default.changeRiderInformation(
    riderAmount,
    riderCode,
    index,
    app.riderInformation.amounts,
    app.riderInformation.codes
  );

  return ValidNewYorkAccident.createApp({
    ...app,
    riderInformation: new RI.default(riderInformation.riderCodes, riderInformation.riderAmounts, riderCodes),
  });
};

export const changeBaseUnits = (baseUnits: string) => (app: NewYorkAccident) =>
  ValidNewYorkAccident.createApp({...app, baseUnits: NonEmptyString.create(baseUnits)});

export type riderCodeType = {code: string; displayName: string; units: number[]};

export const baseUnits = [0.5, 1, 1.5, 2, 2.5, 3];

export const plans = ['AP4', 'AP5'];

export const getAllRiderCodes = () => riderCodes;
